<template>
  <v-container class="bank-statement-container">
    <v-card class="elevation-3 rounded-lg pa-4">
      <div class="bank-statement-faqs">
        <!-- Bank Statement FAQs Section -->
        <h2 class="text-h5 font-weight-bold primary--text mb-4">
          Bank Statement Submission FAQs
        </h2>

        <v-expansion-panels flat>
          <!-- How do I submit my bank statements? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              How do I submit my bank statements for a loan application?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text class="pa-0">
                <p class="body-1 mb-3">
                  At Century Financial Services, we offer
                  <strong>two secure and convenient options</strong> for
                  submitting your bank statements:
                </p>

                <v-card outlined class="mb-4 pa-2">
                  <h3
                    class="text-subtitle-1 font-weight-bold primary--text mb-3"
                  >
                    Option 1: Submit via TruID or Gathr (Fastest & Most Secure)
                  </h3>

                  <p class="body-1 mb-3">
                    We partner with <strong>TruID</strong> and
                    <strong>Gathr—trusted</strong>, consent-driven platforms
                    that allow you to securely connect your bank account and
                    share your statements directly with us.
                  </p>

                  <p class="body-1 mb-2">
                    <strong>How it works:</strong>
                  </p>

                  <v-stepper vertical>
                    <v-stepper-step step="1" complete>
                      <strong>Grant Consent</strong> - Approve the request to
                      access your bank data
                    </v-stepper-step>
                    <v-stepper-content step="1"> </v-stepper-content>

                    <v-stepper-step step="2" complete>
                      <strong>Verify Your Identity</strong> - Confirm your
                      identity through your bank
                    </v-stepper-step>
                    <v-stepper-content step="2"> </v-stepper-content>

                    <v-stepper-step step="3" complete>
                      <strong>Authorise Access</strong> - Authorise the secure
                      sharing of your statements
                    </v-stepper-step>
                    <v-stepper-content step="3"> </v-stepper-content>
                  </v-stepper>

                  <v-card-text class="px-0 pt-4">
                    <p class="body-1 mb-1">
                      <v-icon color="primary">mdi-check-circle</v-icon> Takes
                      <strong>less than a minute</strong> to complete
                    </p>
                    <p class="body-1 mb-1">
                      <v-icon color="primary">mdi-check-circle</v-icon> Fully
                      <strong>compliant with PoPIA</strong>
                      (Protection of Personal Information Act)
                    </p>
                    <p class="body-1 mb-3">
                      <v-icon color="primary">mdi-check-circle</v-icon> Your
                      private information is
                      <strong>never shared or visible</strong> without consent
                    </p>

                    <p class="body-1 mb-2">🔗 Learn More:</p>
                    <ul class="ml-3 mb-0">
                      <li class="body-1 mb-1">
                        <a
                          href="https://www.truid.co.za/"
                          class="text-decoration-none font-weight-medium"
                          target="_blank"
                          >TruID</a
                        >
                      </li>
                      <li class="body-1">
                        <a
                          href="https://finch-technologies.com/"
                          class="text-decoration-none font-weight-medium"
                          target="_blank"
                          >Gathr by Finch Technologies</a
                        >
                      </li>
                    </ul>
                  </v-card-text>
                </v-card>

                <v-card outlined class="mb-3 pa-2">
                  <h3
                    class="text-subtitle-1 font-weight-bold primary--text mb-3"
                  >
                    Option 2: Upload Your Bank Statements Manually
                  </h3>

                  <p class="body-1 mb-3">
                    Prefer not to link your bank account online? No problem. You
                    can submit your statements manually:
                  </p>

                  <v-alert type="info" outlined dense class="mb-3">
                    <div class="d-flex align-center">
                      <span class="mr-2">🧾</span>
                      Upload via our secure Web Form on the Century Financial
                      Services platform
                    </div>
                  </v-alert>

                  <v-alert type="warning" outlined dense>
                    <div class="d-flex align-center">
                      <span class="mr-2">📌</span>
                      Please ensure your bank statements include at least the
                      last 3 months of income
                    </div>
                  </v-alert>
                </v-card>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Is it safe to share my bank statements? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              Is it safe to share my bank statements online?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text>
                <p class="body-1 mb-3">
                  Yes, our statement submission process is designed with your
                  security as our priority:
                </p>

                <v-list dense class="flex-wrap">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-shield-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-1 text-wrap"
                        >TruID and Gathr employ Privacy & Security principles by
                        design</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-shield-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-1 text-wrap"
                        >Service providers guarantee the highest level of
                        security when connecting you to your
                        bank</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-shield-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-1 text-wrap"
                        >Service providers only act as a proxy between you and
                        your bank</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-shield-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-1 text-wrap"
                        >Your private information is not visible, stored or
                        shared with anyone</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Need help submitting bank statements? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              Need help submitting your bank statements?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text>
                <p class="body-1 mb-4">
                  If you're having trouble submitting your bank statements or
                  have any questions, our friendly support team is here to help:
                </p>

                <v-card outlined class="pa-4 mb-4">
                  <v-row align="center" no-gutters>
                    <v-col cols="12" sm="7" class="mb-3 mb-sm-0">
                      <div class="d-flex align-center">
                        <v-icon left color="primary">mdi-email</v-icon>
                        <!-- Mobile Only -->
                        <a
                          href="mailto:clientsupport@centuryfinancialservices.net"
                          class="text-decoration-none font-weight-medium d-inline d-md-none"
                        >
                          clientsupport
                        </a>

                        <!-- Desktop Only -->
                        <a
                          href="mailto:clientsupport@centuryfinancialservices.net"
                          class="text-decoration-none font-weight-medium d-none d-md-inline"
                        >
                          clientsupport@centuryfinancialservices.net
                        </a>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="5" class="mb-3 mb-sm-0">
                      <div class="d-flex align-center">
                        <v-icon left color="primary">mdi-whatsapp</v-icon>
                        <a
                          href="https://wa.me/+270210541057"
                          class="text-decoration-none font-weight-medium"
                        >
                          +27 021 054 1057
                        </a>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>

                <p class="body-1 text-center font-weight-medium primary--text">
                  We're here to assist!
                </p>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "BankStatementComponent",
  data() {
    return {
      // No specific data items needed for this component currently
    };
  },
};
</script>

<style scoped>
.bank-statement-container {
  max-width: 900px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 24px;
}

@media only screen and (max-width: 600px) {
  .body-1 {
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.01em;
  }
}
</style>
